import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Campaign } from "@/components/campaigns/CampaignModal";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { DateRangeFields } from "./DateRangeFields";
import { CampaignValueField, CampaignValueFieldRef } from "./CampaignValueField";
import { CampaignCashbackCustomization } from "./CampaignCashbackCustomization";
import { AdvancedCampaignFields } from "./AdvancedCampaignFields";
import { NuvemshopIntegrationAlert } from "./NuvemshopIntegrationAlert";
import { campaignFormSchema, CampaignFormData } from "./CampaignFormSchema";
import { SmsSection } from "../SmsSection";
import { cashbackTypes, referralTypes, getValueFieldConfig, getAdvancedFieldsConfig } from "./CampaignTypeConfig";
import { parseLocalDate } from "@/utils/dateUtils";
import { Calendar, Settings, FileText, Save, Send } from "lucide-react";

interface CampaignFormProps {
  onSubmit: (values: CampaignFormData, action: 'draft' | 'publish') => void;
  onCancel: () => void;
  campaign?: Campaign;
  isEditing: boolean;
}

export function CampaignForm({ onSubmit, onCancel, campaign, isEditing }: CampaignFormProps) {
  const valueFieldRef = useRef<CampaignValueFieldRef>(null);
  
  const form = useForm<CampaignFormData>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: campaign
      ? {
          name: campaign.name,
          type: campaign.type,
          product: campaign.product,
          value: campaign.value || undefined,
          start_date: parseLocalDate(campaign.start_date),
          end_date: parseLocalDate(campaign.end_date),
          description: campaign.description || "",
          cashback_template_id: campaign.cashback_template_id || undefined,
          max_coupons_per_customer: campaign.max_coupons_per_customer || 1,
          delay_days: campaign.delay_days || 3,
          // Carregar valores de customização de cashback do banco de dados
          use_custom_cashback: campaign.use_custom_cashback || false,
          custom_cashback_percentage: campaign.custom_cashback_percentage || undefined,
          custom_cashback_multiplier: campaign.custom_cashback_multiplier || undefined,
          custom_cashback_expiration_days: campaign.custom_cashback_expiration_days || undefined,
          // Carregar valores de SMS do banco de dados
          sms_message: (campaign as any).sms_message || undefined,
          sms_template_id: (campaign as any).sms_template_id || undefined,
        }
      : {
          name: "",
          type: "",
          product: "cashback",
          value: undefined,
          description: "",
          cashback_template_id: undefined,
          max_coupons_per_customer: 1,
          delay_days: 3,
          use_custom_cashback: false,
          custom_cashback_percentage: undefined,
          custom_cashback_multiplier: undefined,
          custom_cashback_expiration_days: undefined,
        },
  });

  // Get the currently selected product and type
  const selectedProduct = form.watch("product");
  const selectedType = form.watch("type");
  
  // Reset the type field when the product changes, but only for new campaigns
  useEffect(() => {
    // Only reset type if this is NOT an editing scenario or if we're creating a new campaign
    // and the product actually changed from the initial value
    if (!isEditing) {
      form.setValue("type", "");
      // Also reset value when changing product/type for new campaigns
      form.setValue("value", undefined);
    }
  }, [selectedProduct, form, isEditing]);

  // Reset value when type changes for consistency
  useEffect(() => {
    if (!isEditing && selectedType) {
      // Set appropriate default for first_purchase to ensure user fills it
      if (selectedType === 'first_purchase') {
        form.setValue("value", undefined);
      }
    }
  }, [selectedType, form, isEditing]);

  // Get the appropriate campaign types based on selected product
  const campaignTypes = selectedProduct === "cashback" ? cashbackTypes : referralTypes;

  // Determine value field behavior based on selected type
  const valueFieldConfig = getValueFieldConfig(selectedType);
  const advancedFieldsConfig = getAdvancedFieldsConfig(selectedType);

  const handleSubmit = (action: 'draft' | 'publish') => {
    form.handleSubmit((values) => {
      // Força a sincronização do valor do DOM antes da submissão
      if (valueFieldRef.current) {
        valueFieldRef.current.syncValueFromDOM();
      }
      
      // Pega os valores atualizados do formulário
      const currentValues = form.getValues();
      
      onSubmit(currentValues, action);
    })();
  };

  const isDraft = campaign?.status === 'draft';
  const isCashbackProduct = selectedProduct === "cashback";

  return (
    <Form {...form}>
      <div className="space-y-6">
        {/* Alerta de Integração Nuvemshop */}
        <NuvemshopIntegrationAlert />

        {/* Informações Básicas */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-blue-600" />
              Informações Básicas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    Nome da Campanha <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Ex: Campanha de Dia das Mães" 
                      className="h-10"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="product"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Produto <span className="text-red-500">*</span>
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder="Selecione um produto" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="cashback">Cashback</SelectItem>
                        <SelectItem value="referral">Indique e Ganhe</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Tipo de Campanha <span className="text-red-500">*</span>
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder="Selecione um tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {campaignTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">Descrição</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Detalhes adicionais sobre a campanha"
                      className="resize-none min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Período e Configuração */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="h-5 w-5 text-green-600" />
              Período e Configuração
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <DateRangeFields form={form} />
            
            {/* Sempre mostrar o campo de valor se o tipo de campanha estiver selecionado */}
            {selectedType && (
              <>
                <Separator />
                <CampaignValueField 
                  ref={valueFieldRef}
                  form={form} 
                  campaignType={selectedType} 
                  product={selectedProduct} 
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Customização de Cashback - apenas para campanhas de cashback */}
        {isCashbackProduct && (
          <CampaignCashbackCustomization show={true} />
        )}

        {/* Notificação SMS */}
        <SmsSection />

        {/* Configurações Obrigatórias */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Settings className="h-5 w-5 text-purple-600" />
              Configurações Obrigatórias
            </CardTitle>
            <p className="text-sm text-gray-600">
              Configurações adicionais obrigatórias para criar a campanha
            </p>
          </CardHeader>
          <CardContent>
            <AdvancedCampaignFields config={advancedFieldsConfig} />
          </CardContent>
        </Card>
        
        {/* Botões de Ação */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t bg-gray-50/50 -mx-6 px-6 py-4 -mb-6">
          <Button variant="outline" type="button" onClick={onCancel} className="h-10">
            Cancelar
          </Button>
          
          {!isEditing && (
            <Button 
              variant="outline" 
              type="button" 
              onClick={() => handleSubmit('draft')}
              className="h-10 flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              Salvar em Rascunho
            </Button>
          )}
          
          {isEditing && isDraft && (
            <>
              <Button 
                variant="outline" 
                type="button" 
                onClick={() => handleSubmit('draft')}
                className="h-10 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                Salvar alterações
              </Button>
              
              <Button 
                type="button" 
                variant="brand"
                onClick={() => handleSubmit('publish')}
                className="h-10 flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                Publicar Campanha
              </Button>
            </>
          )}
          
          {isEditing && !isDraft && (
            <Button 
              type="button" 
              variant="brand"
              onClick={() => handleSubmit('draft')}
              className="h-10 flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              Salvar alterações
            </Button>
          )}
          
          {!isEditing && (
            <Button 
              type="button" 
              variant="brand"
              onClick={() => handleSubmit('publish')}
              className="h-10 flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              Publicar Campanha
            </Button>
          )}
        </div>
      </div>
    </Form>
  );
}
